import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SkeletonHero } from '../components/ui/page-skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { DataTable } from '../components/DataTable';
import RoyaltyPanel from '../components/finance/RoyaltyPanel';
import { ComingSoonPanel } from '../components/ComingSoonPanel';
import {
  ArrowLeft, GraduationCap, Receipt, Money,
  Lightning, Drop, Plus, FloppyDisk, Spinner, Trash,
  PencilSimple, Storefront, Users as UsersIcon, ChartPieSlice, ChartLineUp,
  Buildings, Phone, EnvelopeSimple, Bank, IdentificationCard,
  MapPin, CurrencyDollar, Clock, User as UserIcon,
  FileText, FilePdf, DownloadSimple, Eye,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BILL_TYPES = [
  { value: 'water',       label: 'Water',       icon: Drop,      color: 'text-blue-700',  bg: 'bg-blue-100',  ring: 'ring-blue-300' },
  { value: 'electricity', label: 'Electricity', icon: Lightning, color: 'text-amber-700', bg: 'bg-amber-100', ring: 'ring-amber-300' },
];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const ROYALTY_STATUSES = [
  { value: 'paid',    label: 'Paid',    bg: 'bg-emerald-100', text: 'text-emerald-800', ring: 'ring-emerald-300' },
  { value: 'owed',    label: 'Owed',    bg: 'bg-amber-100',   text: 'text-amber-800',   ring: 'ring-amber-300'   },
  { value: 'overdue', label: 'Overdue', bg: 'bg-rose-100',    text: 'text-rose-800',    ring: 'ring-rose-300'    },
];

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function CampusDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const { entities } = useTenant();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [campus, setCampus] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});

  const fetchCampus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/campuses`, { headers: authHeaders(), withCredentials: true });
      const c = (Array.isArray(data) ? data : []).find(x => x.id === id);
      if (!c) { toast.error('Campus not found'); navigate('/campuses'); return; }
      setCampus(c);
      setForm({
        campus_name: c.campus_name || '',
        short_name: c.short_name || '',
        entity_id: c.entity_id || '',
        physical_addr: c.physical_addr || '',
        address_line1: c.address_line1 || '',
        address_line2: c.address_line2 || '',
        telephone: c.telephone || '',
        email: c.email || '',
        bank_name: c.bank_name || '',
        bank_acc_name: c.bank_acc_name || '',
        bank_acc_no: c.bank_acc_no || '',
        subscription_fee: c.subscription_fee || '',
        initial_recharge: c.initial_recharge || '',
      });
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load campus'));
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchCampus(); }, [fetchCampus]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/campuses/${id}`, form, { headers: authHeaders(), withCredentials: true });
      toast.success('Campus updated');
      setEditOpen(false);
      await fetchCampus();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to update campus'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !campus) {
    return <div className="py-2"><SkeletonHero testid="campus-detail-loading" /></div>;
  }

  return (
    <div className="space-y-4 pb-6" data-testid="campus-detail-page">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/campuses')} className="h-8 w-8 p-0 mt-1" data-testid="back-btn">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <span className="w-12 h-12 rounded-xl bg-[#0A2540]/10 flex items-center justify-center shrink-0">
            <GraduationCap size={22} className="text-[#0A2540]" weight="duotone" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-[#1C1917] tracking-tight truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {campus.campus_name}
              </h1>
              {campus.status && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset ${
                  String(campus.status).toLowerCase() === 'active'
                    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                    : 'bg-slate-50 text-slate-600 ring-slate-200'
                }`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {campus.status}
                </span>
              )}
            </div>
            <p className="text-xs text-[#78716C] truncate font-mono mt-1">
              {[
                campus.campus_code,
                campus.short_name,
                // Hide entity name for entity-scoped users — they only see
                // their own entity so the suffix is noise.
                isSuperAdmin ? campus.entity_name : null,
              ].filter(Boolean).join(' · ') || '—'}
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
        <TabsList className="bg-[#FCFAF6] rounded-xl p-1 h-auto">
          <TabsTrigger value="overview" className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-overview">
            <ChartPieSlice size={14} weight="duotone" /> Overview
          </TabsTrigger>
          <TabsTrigger value="details" className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-details">
            <GraduationCap size={14} weight="duotone" /> Campus Details
          </TabsTrigger>
          <TabsTrigger value="bills" className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-bills">
            <Receipt size={14} weight="duotone" /> Utility Bills
          </TabsTrigger>
          <TabsTrigger value="royalties" className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-royalties">
            <Money size={14} weight="duotone" /> Royalties
          </TabsTrigger>
        </TabsList>

        <div className="rounded-2xl border border-[#EDE6D8] bg-[#FCFAF6]/30 p-3 sm:p-4" data-testid="campus-tab-frame">
          {/* ── OVERVIEW (default landing) ── */}
          <TabsContent value="overview" className="mt-0">
            <CampusOverview campus={campus} />
          </TabsContent>

          {/* ── CAMPUS DETAILS (read-only view) ── */}
          <TabsContent value="details" className="mt-0">
            <CampusDetailsView
              campus={campus}
              isSuperAdmin={isSuperAdmin}
              onEdit={() => setEditOpen(true)}
            />
          </TabsContent>

          <TabsContent value="bills" className="mt-0">
            <UtilityBillsScoped campus={campus} />
          </TabsContent>
          <TabsContent value="royalties" className="mt-0">
            <RoyaltyPanel scope="campus" campusId={campus.id} testIdPrefix="campus-royalty" />
          </TabsContent>
        </div>
      </Tabs>

      {/* ── EDIT CAMPUS DIALOG ── */}
      <Dialog open={editOpen} onOpenChange={(o) => setEditOpen(o)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Edit Campus</DialogTitle>
            <DialogDescription>Update campus properties. Changes sync to ecobridge.tech.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-6">
            {/* Basic */}
            <Section title="Basic Information">
              <div className="grid grid-cols-2 gap-4">
                <FField label="Campus Name *" value={form.campus_name} onChange={v => setForm(p => ({ ...p, campus_name: v }))} testid="d-campus-name" />
                <FField label="Short Name *"  value={form.short_name}  onChange={v => setForm(p => ({ ...p, short_name: v }))}  testid="d-short" />
                {isSuperAdmin && (
                  <div className="space-y-2 col-span-2">
                    <Label>Entity</Label>
                    <Select value={form.entity_id} onValueChange={v => setForm(p => ({ ...p, entity_id: v }))}>
                      <SelectTrigger data-testid="d-entity"><SelectValue placeholder="Select entity" /></SelectTrigger>
                      <SelectContent>
                        {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </Section>
            {/* Address */}
            <Section title="Address & Contact">
              <div className="grid grid-cols-2 gap-4">
                <FField label="Physical Address" value={form.physical_addr} onChange={v => setForm(p => ({ ...p, physical_addr: v }))} testid="d-physical" />
                <FField label="Address Line 1"   value={form.address_line1} onChange={v => setForm(p => ({ ...p, address_line1: v }))} testid="d-line1" />
                <FField label="Address Line 2"   value={form.address_line2} onChange={v => setForm(p => ({ ...p, address_line2: v }))} testid="d-line2" />
                <FField label="Telephone"        value={form.telephone}     onChange={v => setForm(p => ({ ...p, telephone: v }))}     testid="d-tel" />
                <div className="col-span-2"><FField label="Email" type="email" value={form.email} onChange={v => setForm(p => ({ ...p, email: v }))} testid="d-email" /></div>
              </div>
            </Section>
            {/* Banking */}
            <Section title="Banking Details">
              <div className="grid grid-cols-2 gap-4">
                <FField label="Bank Name"     value={form.bank_name}     onChange={v => setForm(p => ({ ...p, bank_name: v }))}     testid="d-bank-name" />
                <FField label="Account Name"  value={form.bank_acc_name} onChange={v => setForm(p => ({ ...p, bank_acc_name: v }))} testid="d-bank-an" />
                <div className="col-span-2"><FField label="Account Number" value={form.bank_acc_no} onChange={v => setForm(p => ({ ...p, bank_acc_no: v }))} testid="d-bank-no" /></div>
              </div>
            </Section>
            {/* Pricing */}
            <Section title="Pricing">
              <div className="grid grid-cols-2 gap-4">
                <FField label="Subscription Fee" type="number" value={form.subscription_fee} onChange={v => setForm(p => ({ ...p, subscription_fee: v }))} testid="d-fee" />
                <FField label="Initial Recharge" type="number" value={form.initial_recharge} onChange={v => setForm(p => ({ ...p, initial_recharge: v }))} testid="d-recharge" />
              </div>
            </Section>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="save-campus-btn">
                {saving ? <Spinner size={14} className="animate-spin mr-2" /> : <FloppyDisk size={14} className="mr-2" />}
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// CAMPUS OVERVIEW DASHBOARD
// ──────────────────────────────────────────────────────────────────────────
import {
  AreaChart as ReAreaChart, Area as ReArea,
  BarChart as ReBarChart, Bar as ReBar,
  XAxis as ReXAxis, YAxis as ReYAxis,
  CartesianGrid as ReCartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer as ReResponsiveContainer, Cell as ReCell,
  PieChart as RePieChart, Pie as RePie, Legend as ReLegend,
} from 'recharts';
import { SkBar } from '../components/ui/page-skeleton';

function CampusOverview({ campus }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const currency = campus.entity_currency || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/campuses/${campus.id}/analytics`,
          { headers: authHeaders(), withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, 'Failed to load campus analytics'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campus.id]);

  if (loading) return <CampusOverviewSkeleton />;
  if (!data) return <p className="py-12 text-center text-sm text-[#78716C]">No analytics available.</p>;

  const fmt  = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtI = (n) => Number(n || 0).toLocaleString();
  const fmtL = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} L`;
  const compact = (n) => {
    const v = Math.abs(Number(n || 0));
    if (v >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
    return String(Math.round(n || 0));
  };

  const h = data.headline || {};
  const monthly = data.monthly || [];
  const daily   = data.daily   || [];
  const peers   = data.peer_comparison || [];
  const peerMax = Math.max(...peers.map(p => p.revenue || 0), 0) || 1;
  const topStores = data.top_stores || [];
  const topBuyers = data.top_buyers || [];
  const perf = data.performance || { excellent: 0, good: 0, normal: 0, dormant: 0 };
  const perfData = [
    { name: 'Excellent', value: perf.excellent, color: '#7C3AED' },
    { name: 'Good',      value: perf.good,      color: '#059669' },
    { name: 'Normal',    value: perf.normal,    color: '#C9A35B' },
    { name: 'Dormant',   value: perf.dormant,   color: '#94A3B8' },
  ].filter(p => p.value > 0);

  const tooltipStyle = {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    border: '1px solid #EDE6D8',
    borderRadius: '10px',
    fontSize: 12,
    boxShadow: '0 4px 16px -8px rgba(28,25,23,0.12)',
  };

  return (
    <div className="space-y-4" data-testid="campus-overview">
      {/* Hero KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CampusKpi label="Total Stores"       value={fmtI(h.total_stores)}                          icon={Storefront}    accent="#0A2540" />
        <CampusKpi label="Total Subscribers"  value={fmtI(h.total_subscribers)}                     icon={UsersIcon}     accent="#0A2540" />
        <CampusKpi label="Revenue · Today"    value={fmt(h.today_revenue)}    unit={currency}       icon={CurrencyDollar} accent="#0A2540" />
        <CampusKpi label="Revenue · Lifetime" value={fmt(h.lifetime_revenue)} unit={currency}       icon={ChartPieSlice} accent="#0A2540" />
      </div>

      {/* Revenue trend (Past 6 months) */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]"><Receipt size={14} weight="fill" /></span>
            <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Revenue Trend · Past 6 Months</p>
          </div>
          <p className="text-[11px] font-mono text-[#78716C]">in {currency || '—'}</p>
        </div>
        {monthly.every(m => !m.revenue) ? (
          <div className="h-[220px] flex items-center justify-center"><p className="text-xs text-[#78716C]">No revenue recorded in the past 6 months.</p></div>
        ) : (
          <div className="h-[220px]">
            <ReResponsiveContainer width="100%" height="100%">
              <ReBarChart data={monthly} margin={{ top: 16, right: 8, left: -8, bottom: 0 }}>
                <ReCartesianGrid strokeDasharray="3 3" stroke="#EDE6D8" vertical={false} />
                <ReXAxis dataKey="label" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} />
                <ReYAxis tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} tickFormatter={compact} />
                <ReTooltip
                  contentStyle={tooltipStyle}
                  labelStyle={{ color: '#0A2540', fontWeight: 600, marginBottom: 4 }}
                  formatter={(v, _n, ctx) => {
                    const r = ctx?.payload || {};
                    return [
                      `${fmt(v)} ${currency}\nRecharges ${compact(r.recharges)} · Subs Fees ${compact(r.subs_fees)} · Renewals ${compact(r.renewal_fees)}`,
                      'Revenue',
                    ];
                  }}
                />
                <ReBar dataKey="revenue" radius={[8, 8, 0, 0]} maxBarSize={72}>
                  {monthly.map((_, i) => (<ReCell key={i} fill={i === monthly.length - 1 ? '#0A2540' : '#C9A35B'} />))}
                </ReBar>
              </ReBarChart>
            </ReResponsiveContainer>
          </div>
        )}
      </div>

      {/* Daily revenue (30 days) + Subscriber performance donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white border border-[#EDE6D8] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#059669]/10 text-[#059669]"><ChartLineUp size={14} weight="fill" /></span>
            <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Daily Revenue · Past 30 Days</p>
          </div>
          {daily.every(d => !d.revenue) ? (
            <div className="h-[200px] flex items-center justify-center"><p className="text-xs text-[#78716C]">No revenue in the last 30 days.</p></div>
          ) : (
            <div className="h-[200px]">
              <ReResponsiveContainer width="100%" height="100%">
                <ReAreaChart data={daily} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="campusDailyGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <ReCartesianGrid strokeDasharray="3 3" stroke="#EDE6D8" vertical={false} />
                  <ReXAxis dataKey="label" tick={{ fontSize: 9, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} interval={3} />
                  <ReYAxis tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} tickFormatter={compact} />
                  <ReTooltip contentStyle={tooltipStyle} formatter={(v) => [`${fmt(v)} ${currency}`, 'Revenue']} labelStyle={{ color: '#0A2540', fontWeight: 600 }} />
                  <ReArea type="monotone" dataKey="revenue" stroke="#059669" strokeWidth={2} fill="url(#campusDailyGrad)" />
                </ReAreaChart>
              </ReResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#7C3AED]/10 text-[#7C3AED]"><UsersIcon size={14} weight="fill" /></span>
            <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Subscriber Performance</p>
          </div>
          {perfData.length === 0 ? (
            <div className="h-[180px] flex items-center justify-center"><p className="text-xs text-[#78716C]">No subscribers.</p></div>
          ) : (
            <>
              <div className="h-[170px]">
                <ReResponsiveContainer width="100%" height="100%">
                  <RePieChart>
                    <RePie data={perfData} dataKey="value" cx="50%" cy="50%" innerRadius={42} outerRadius={64} paddingAngle={3} stroke="none">
                      {perfData.map((s, i) => (<ReCell key={i} fill={s.color} />))}
                    </RePie>
                    <ReTooltip contentStyle={tooltipStyle} formatter={(v, name) => [`${fmtI(v)} subscribers`, name]} />
                  </RePieChart>
                </ReResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-1.5 mt-1">
                {perfData.map((p) => (
                  <div key={p.name} className="flex items-center gap-2 text-[11px]">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: p.color }} />
                    <span className="text-[#1C1917] font-semibold flex-1 truncate">{p.name}</span>
                    <span className="text-[#78716C] font-mono">{p.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Averages + Peer comparison stats strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CampusKpi label="Avg Monthly Revenue" value={fmt(data.averages?.avg_monthly_revenue || 0)} unit={currency} sub="6-month avg" icon={ChartLineUp} accent="#0A2540" small />
        <CampusKpi label="Avg Daily Revenue"   value={fmt(data.averages?.avg_daily_revenue   || 0)} unit={currency} sub="30-day avg"  icon={ChartLineUp} accent="#0A2540" small />
        <CampusKpi label="Peer Rank" value={h.rank ? `#${h.rank}` : '—'} sub={h.peer_count ? `of ${fmtI(h.peer_count)} campuses` : 'no peers'} icon={ChartPieSlice} accent="#0A2540" small />
        <CampusKpi label="Revenue Share" value={`${(h.share_pct || 0).toFixed(1)}%`} sub="of entity revenue" icon={ChartPieSlice} accent="#0A2540" small />
      </div>

      {/* Peer comparison bars + Top stores */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white border border-[#EDE6D8] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]"><ChartPieSlice size={14} weight="fill" /></span>
            <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>vs Sibling Campuses · Lifetime Revenue</p>
          </div>
          {peers.length === 0 ? (
            <p className="text-xs text-[#78716C] py-8 text-center">No peer campuses.</p>
          ) : (
            <div className="space-y-2.5">
              {peers.map((p) => {
                const pct = (p.revenue / peerMax) * 100;
                return (
                  <div key={p.campus_id}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className={`truncate ${p.is_self ? 'font-bold text-[#0A2540]' : 'text-[#1C1917]'}`}>{p.campus}</span>
                      </div>
                      <span className="font-mono text-[#78716C] shrink-0">{fmt(p.revenue)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#FCFAF6] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: p.is_self ? '#0A2540' : '#C9A35B' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0EA5E9]/10 text-[#0EA5E9]"><Storefront size={14} weight="fill" /></span>
            <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Top Stores · Lifetime</p>
          </div>
          {topStores.length === 0 ? (
            <p className="text-xs text-[#78716C] py-8 text-center">No store sales yet.</p>
          ) : (
            <div className="space-y-2.5">
              {topStores.map((s, i) => (
                <div key={s.store_name || i} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-mono text-[#78716C] font-semibold w-5 shrink-0">#{i + 1}</span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[#1C1917] truncate">{s.store_name}</p>
                      <p className="text-[10px] text-[#78716C] font-mono">{fmtI(s.txns)} txn · {fmtL(s.volume)}</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-[#0A2540] font-mono shrink-0">{compact(s.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top buyers (full width) */}
      {topBuyers.length > 0 && (
        <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#059669]/10 text-[#059669]"><UsersIcon size={14} weight="fill" /></span>
            <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Top Buyers · Lifetime</p>
          </div>
          <div className="space-y-2">
            {topBuyers.map((b, i) => (
              <div key={b.sub_id || i} className="grid grid-cols-[28px_1fr_auto] items-center gap-3">
                <span className="text-[10px] font-mono text-[#78716C] font-semibold">#{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1C1917] truncate">{b.name}</p>
                  <p className="text-[10px] text-[#78716C] font-mono">Card {b.card_no || '—'} · {fmtI(b.txns)} txns</p>
                </div>
                <span className="text-sm font-mono font-bold text-[#059669] shrink-0">{fmt(b.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CampusKpi({ label, value, unit, sub, icon: Icon, accent = '#0A2540', small }) {
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
          <p className={`mt-1.5 font-bold font-mono leading-tight truncate ${small ? 'text-xl' : 'text-2xl'}`} style={{ color: accent }}>
            {value}
            {unit && <span className="ml-1 text-[10px] font-semibold text-[#78716C]">{unit}</span>}
          </p>
          {sub && <p className="text-[11px] text-[#78716C] mt-0.5 font-mono truncate">{sub}</p>}
        </div>
        <span className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center" style={{ backgroundColor: `${accent}1A`, color: accent }}>
          <Icon size={16} weight="fill" />
        </span>
      </div>
    </div>
  );
}

function CampusOverviewSkeleton() {
  return (
    <div className="space-y-4" data-testid="campus-overview-loading">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <SkBar className="h-3 w-1/3 mb-2" />
                <SkBar className="h-6 w-2/3 mb-2" />
                <SkBar className="h-3 w-1/2" />
              </div>
              <SkBar className="h-9 w-9 rounded-xl shrink-0" />
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
        <SkBar className="h-4 w-48 mb-3" />
        <div className="h-[220px] flex items-end justify-between gap-3">
          {[0.4, 0.7, 0.55, 0.85, 0.45, 0.95].map((h, i) => (
            <SkBar key={i} className="flex-1 rounded-t-md" style={{ height: `${h * 100}%` }} />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white border border-[#EDE6D8] rounded-2xl p-5">
          <SkBar className="h-3 w-40 mb-4" />
          <SkBar className="h-[200px] w-full rounded-md" />
        </div>
        <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
          <SkBar className="h-3 w-40 mb-4" />
          <SkBar className="h-[170px] w-full rounded-md" />
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
            <SkBar className="h-3 w-1/3 mb-2" />
            <SkBar className="h-5 w-2/3" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// CAMPUS DETAILS — Read-only view of all middleware fields
// ──────────────────────────────────────────────────────────────────────────
function CampusDetailsView({ campus, isSuperAdmin, onEdit }) {
  const fmtMoney = (v) => {
    const n = Number(v ?? 0);
    if (Number.isNaN(n)) return '—';
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const fmtDate = (s) => {
    if (!s) return '—';
    const m = String(s).match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
    return m ? `${m[1]} · ${m[2]}` : String(s);
  };
  return (
    <div className="space-y-4">
      {/* Edit-only action card */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-4 flex items-center justify-end">
        <Button onClick={onEdit} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg" data-testid="campus-edit-btn">
          <PencilSimple size={14} className="mr-2" />
          Edit Campus
        </Button>
      </div>

      {/* Basic + Assignment */}
      <SectionCard title="Basic Information" icon={GraduationCap}>
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${isSuperAdmin ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
          <Cell icon={GraduationCap}    label="Campus Name"  value={campus.campus_name} />
          <Cell icon={IdentificationCard} label="Short Name" value={campus.short_name} mono />
          <Cell icon={IdentificationCard} label="Campus Code" value={campus.campus_code} mono />
          {isSuperAdmin && <Cell icon={Buildings} label="Entity" value={campus.entity_name} />}
        </div>
      </SectionCard>

      {/* Address & Contact */}
      <SectionCard title="Address & Contact" icon={MapPin}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Cell icon={MapPin}          label="Physical Address" value={campus.physical_addr} />
          <Cell icon={MapPin}          label="Address Line 1"   value={campus.address_line1} />
          <Cell icon={MapPin}          label="Address Line 2"   value={campus.address_line2} />
          <Cell icon={Phone}           label="Telephone"        value={campus.telephone} mono />
          <div className="lg:col-span-2"><Cell icon={EnvelopeSimple} label="Email" value={campus.email} mono /></div>
        </div>
      </SectionCard>

      {/* Banking */}
      <SectionCard title="Banking Details" icon={Bank}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Cell icon={Bank}             label="Bank Name"      value={campus.bank_name} />
          <Cell icon={UserIcon}         label="Account Name"   value={campus.bank_acc_name} />
          <Cell icon={IdentificationCard} label="Account Number" value={campus.bank_acc_no} mono />
        </div>
      </SectionCard>

      {/* Pricing */}
      <SectionCard title="Pricing" icon={CurrencyDollar}>
        <div className="grid grid-cols-2 gap-3">
          <MoneyCell icon={CurrencyDollar} label="Subscription Fee" value={fmtMoney(campus.subscription_fee)} />
          <MoneyCell icon={CurrencyDollar} label="Initial Recharge" value={fmtMoney(campus.initial_recharge)} />
        </div>
      </SectionCard>

      {/* Audit */}
      <SectionCard title="Audit Trail" icon={Clock}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Cell icon={UserIcon} label="Maker ID"        value={campus.maker_id} mono />
          <Cell icon={Clock}    label="Date Registered" value={fmtDate(campus.date_registered || campus.created_at)} mono />
          <Cell icon={Clock}    label="Date Updated"    value={fmtDate(campus.date_updated)}    mono />
        </div>
      </SectionCard>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, sub, accent = '#0A2540' }) {
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
      <div className="flex items-start justify-between">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
        <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}1A`, color: accent }}>
          <Icon size={14} weight="fill" />
        </span>
      </div>
      <p className="mt-1.5 text-2xl font-bold font-mono" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-[11px] text-[#78716C] mt-0.5 font-mono">{sub}</p>}
    </div>
  );
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]">
          <Icon size={14} weight="fill" />
        </span>
        <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{title}</p>
      </div>
      {children}
    </div>
  );
}

function Cell({ icon: Icon, label, value, mono }) {
  const v = (value == null || value === '') ? '—' : value;
  return (
    <div className="rounded-xl border border-[#EDE6D8] bg-[#FCFAF6]/40 p-3">
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={11} weight="duotone" className="text-[#78716C]" />}
        <p className="text-[10px] uppercase tracking-[0.14em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {label}
        </p>
      </div>
      <p
        className={`text-sm font-semibold text-[#1C1917] truncate ${mono ? 'font-mono' : ''}`}
        style={mono ? undefined : { fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        title={typeof v === 'string' ? v : undefined}
      >
        {v}
      </p>
    </div>
  );
}

function MoneyCell({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-[#EDE6D8] bg-[#FCFAF6]/40 p-3">
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={11} weight="duotone" className="text-[#78716C]" />}
        <p className="text-[10px] uppercase tracking-[0.14em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {label}
        </p>
      </div>
      <p className="text-base font-bold text-[#0A2540] font-mono">{value}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {title}
      </p>
      {children}
    </div>
  );
}

function FField({ label, value, onChange, type = 'text', testid }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="border-[#EDE6D8] rounded-lg" data-testid={testid} />
    </div>
  );
}

/* ====== UTILITY BILLS (meter-reading based) ====== */
function UtilityBillsScoped({ campus }) {
  const [subView, setSubView] = useState('bills'); // 'bills' | 'invoices'

  return (
    <div className="space-y-4">
      {/* Sub-tab nav: Create Bill / Invoices */}
      <div className="inline-flex items-center gap-1 bg-white border border-[#EDE6D8] rounded-xl p-1" data-testid="utility-subnav">
        <button
          type="button"
          onClick={() => setSubView('bills')}
          data-testid="subtab-bills"
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
            subView === 'bills'
              ? 'bg-[#0A2540] text-white'
              : 'text-[#1C1917] hover:bg-[#FCFAF6]'
          }`}
        >
          <Receipt size={14} weight="duotone" /> Create Bill
        </button>
        <button
          type="button"
          onClick={() => setSubView('invoices')}
          data-testid="subtab-invoices"
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
            subView === 'invoices'
              ? 'bg-[#0A2540] text-white'
              : 'text-[#1C1917] hover:bg-[#FCFAF6]'
          }`}
        >
          <FileText size={14} weight="duotone" /> Invoices
        </button>
      </div>

      {subView === 'bills' ? <BillsPanel campus={campus} /> : <InvoicesPanel campus={campus} />}
    </div>
  );
}

function BillsPanel({ campus }) {
  const [bills, setBills] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Default billing month = current YYYY-MM
  const defaultMonth = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();

  const [form, setForm] = useState({
    bill_type: 'water',
    store_id: '',
    meter_reading: '',
    billing_month: defaultMonth,
  });

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/campuses/${campus.id}/utility-bills`,
        { headers: authHeaders(), withCredentials: true });
      setBills(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load bills'));
    } finally { setLoading(false); }
  }, [campus.id]);

  const fetchStores = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/stores?campus_id=${campus.id}`,
        { headers: authHeaders(), withCredentials: true });
      setStores(Array.isArray(data) ? data : []);
    } catch (e) { setStores([]); }
  }, [campus.id]);

  useEffect(() => { fetchBills(); fetchStores(); }, [fetchBills, fetchStores]);

  // Build a 24-month picker spanning ±12 months from today.
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 12; i >= -12; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
      opts.push({ value: key, label });
    }
    return opts;
  }, []);

  const totals = useMemo(() => {
    return bills.reduce((a, b) => {
      const t = Number(b.amount || 0);
      a.total += t;
      a[b.bill_type] = (a[b.bill_type] || 0) + t;
      return a;
    }, { total: 0, water: 0, electricity: 0 });
  }, [bills]);

  const reset = () => setForm({
    bill_type: 'water',
    store_id: '',
    meter_reading: '',
    billing_month: defaultMonth,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.store_id) { toast.error('Select a store'); return; }
    if (form.meter_reading === '' || isNaN(Number(form.meter_reading))) {
      toast.error('Enter a valid meter reading'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/campuses/${campus.id}/utility-bills`,
        {
          bill_type: form.bill_type,
          store_id: form.store_id,
          meter_reading: Number(form.meter_reading),
          billing_month: form.billing_month,
        },
        { headers: authHeaders(), withCredentials: true });
      toast.success('Bill recorded');
      setOpen(false); reset();
      fetchBills();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save'));
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm('Delete this bill entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/campuses/${campus.id}/utility-bills/${entry.id}`,
        { headers: authHeaders(), withCredentials: true });
      toast.success('Deleted');
      fetchBills();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete'));
    }
  };

  const fmtN = (v, digits = 2) => Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  });
  const fmtMonth = (key) => {
    if (!key) return '—';
    const m = String(key).match(/^(\d{4})-(\d{2})/);
    if (!m) return key;
    return `${MONTHS[Number(m[2]) - 1] || m[2]} ${m[1]}`;
  };

  // Selected-store rate preview (live during form fill).
  const selectedStore = stores.find(s => String(s.id) === String(form.store_id));
  const previewRate = selectedStore
    ? (form.bill_type === 'water' ? selectedStore.utility_water : selectedStore.utility_electricity)
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#78716C]">Log meter readings per store. Usage × store rate auto-computes the bill total.</p>
        <Button onClick={() => setOpen(true)} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="add-bill-btn">
          <Plus size={14} className="mr-2" /> Add Bill
        </Button>
      </div>

      {/* Stats frame */}
      <div className="rounded-2xl border border-[#EDE6D8] bg-white p-3 sm:p-4" data-testid="utility-stats-frame">
        <div className="grid grid-cols-3 gap-3">
          <KpiCard label="Total Billed"  value={totals.total}       accent="text-[#0A2540]" />
          <KpiCard label="Water"         value={totals.water}       accent="text-blue-700" />
          <KpiCard label="Electricity"   value={totals.electricity} accent="text-amber-700" />
        </div>
      </div>

      {/* Logs frame */}
      <div className="rounded-2xl border border-[#EDE6D8] bg-white p-3 sm:p-4" data-testid="utility-logs-frame">
        <DataTable
          columns={[
            { key: 'billing_month', label: 'Billing Month', width: 'minmax(130px,1fr)', mono: true, tone: 'strong',
              render: (b) => fmtMonth(b.billing_month || (b.year && b.month ? `${b.year}-${b.month}` : '')) },
            { key: 'store_name', label: 'Store', width: 'minmax(140px,1.2fr)', tone: 'strong', render: (b) => b.store_name || '—' },
            { key: 'bill_type', label: 'Utility', width: 'minmax(130px,1fr)', render: (b) => {
              const t = BILL_TYPES.find(x => x.value === b.bill_type) || BILL_TYPES[0];
              return (
                <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full ring-1 ring-inset ${t.bg} ${t.color} ${t.ring}`}>
                  {t.label}
                </span>
              );
            } },
            { key: 'meter_reading', label: 'Current Reading', width: 'minmax(120px,0.9fr)', mono: true, tone: 'strong', align: 'right', render: (b) => fmtN(b.meter_reading) },
            { key: 'usage',  label: 'Usage', width: 'minmax(100px,0.8fr)', mono: true, tone: 'muted', align: 'right', render: (b) => fmtN(b.usage) },
            { key: 'rate',   label: 'Rate',  width: 'minmax(100px,0.8fr)', mono: true, tone: 'muted', align: 'right', render: (b) => fmtN(b.rate) },
            { key: 'amount', label: 'Total', width: 'minmax(120px,1fr)',   mono: true, align: 'right',
              render: (b) => <span className="font-semibold text-[#0A2540]">{fmtN(b.amount)}</span> },
          ]}
          rows={bills}
          loading={loading}
          rowKey={(b) => b.id}
          emptyIcon={<Receipt size={20} className="text-slate-500" />}
          emptyTitle="No utility bills yet"
          emptyText='Click "Add Bill" to record a meter reading for a store.'
          testIdPrefix="bill"
          actionRender={(b) => (
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(b); }}
              className="text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10 h-8 w-8 p-0" aria-label="Delete">
              <Trash size={14} />
            </Button>
          )}
        />
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Utility Bill</DialogTitle>
            <DialogDescription>Log a meter reading for a store at {campus.campus_name}. Usage and total auto-compute from the previous reading and the store's configured rate.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Utility Type</Label>
                <Select value={form.bill_type} onValueChange={(v) => setForm(p => ({ ...p, bill_type: v }))}>
                  <SelectTrigger data-testid="bill-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BILL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Store</Label>
                <Select value={form.store_id} onValueChange={(v) => setForm(p => ({ ...p, store_id: v }))}>
                  <SelectTrigger data-testid="bill-store"><SelectValue placeholder={stores.length ? 'Select store' : 'No stores in campus'} /></SelectTrigger>
                  <SelectContent>
                    {stores.map(s => <SelectItem key={s.id} value={String(s.id)}>{s.store_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Meter Reading</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.meter_reading}
                  onChange={(e) => setForm(p => ({ ...p, meter_reading: e.target.value }))}
                  placeholder="0"
                  data-testid="bill-meter-reading"
                />
              </div>
              <div className="space-y-2">
                <Label>Billing Month</Label>
                <Select value={form.billing_month} onValueChange={(v) => setForm(p => ({ ...p, billing_month: v }))}>
                  <SelectTrigger data-testid="bill-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {selectedStore && (
              <div className="rounded-lg bg-[#FCFAF6] border border-[#EDE6D8] px-3 py-2 text-[11px] font-mono text-[#78716C] flex items-center justify-between">
                <span>Configured rate for <b className="text-[#1C1917]">{selectedStore.store_name}</b></span>
                <span className="text-[#0A2540] font-bold">{fmtN(previewRate || 0)} / unit</span>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="bill-save">
                {submitting ? <Spinner size={14} className="animate-spin mr-2" /> : null}
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ====== UTILITY INVOICES ====== */
const INVOICE_STATUSES = [
  { value: 'issued', label: 'Issued', bg: 'bg-amber-100',   text: 'text-amber-800',   ring: 'ring-amber-300'   },
  { value: 'paid',   label: 'Paid',   bg: 'bg-emerald-100', text: 'text-emerald-800', ring: 'ring-emerald-300' },
];

function InvoicesPanel({ campus }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genOpen, setGenOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [statusInv, setStatusInv] = useState(null);   // invoice being updated
  const [statusForm, setStatusForm] = useState({ status: 'paid', paid_on: '' });
  const [savingStatus, setSavingStatus] = useState(false);
  const [viewInv, setViewInv] = useState(null);

  const defaultMonth = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();
  const [genForm, setGenForm] = useState({ billing_month: defaultMonth, notes: '' });

  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 12; i >= -12; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
      opts.push({ value: key, label });
    }
    return opts;
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/campuses/${campus.id}/invoices`,
        { headers: authHeaders(), withCredentials: true });
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load invoices'));
    } finally { setLoading(false); }
  }, [campus.id]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      await axios.post(`${API_URL}/api/campuses/${campus.id}/invoices`,
        { billing_month: genForm.billing_month, notes: genForm.notes },
        { headers: authHeaders(), withCredentials: true });
      toast.success('Invoice generated');
      setGenOpen(false);
      setGenForm({ billing_month: defaultMonth, notes: '' });
      fetchInvoices();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to generate invoice'));
    } finally { setGenerating(false); }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_no}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/campuses/${campus.id}/invoices/${inv.id}`,
        { headers: authHeaders(), withCredentials: true });
      toast.success('Deleted');
      fetchInvoices();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete'));
    }
  };

  const handleDownload = async (inv) => {
    setDownloadingId(inv.id);
    try {
      const res = await axios.get(`${API_URL}/api/campuses/${campus.id}/invoices/${inv.id}/pdf`,
        { headers: authHeaders(), withCredentials: true, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_no || 'invoice'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to download PDF'));
    } finally { setDownloadingId(null); }
  };

  const openStatus = (inv) => {
    const today = new Date().toISOString().slice(0, 10);
    // Sensible next-step default: issued → sent, sent → paid, paid → keep paid
    const nextStatus = inv.status === 'paid' ? 'paid' : 'paid';
    setStatusInv(inv);
    setStatusForm({ status: nextStatus, paid_on: inv.paid_on || today });
  };

  const handleStatusSave = async (e) => {
    e.preventDefault();
    if (!statusInv) return;
    if (statusForm.status === 'paid' && !statusForm.paid_on) {
      toast.error('Pick a payment date');
      return;
    }
    setSavingStatus(true);
    try {
      const payload = { status: statusForm.status };
      if (statusForm.status === 'paid') payload.paid_on = statusForm.paid_on;
      await axios.patch(
        `${API_URL}/api/campuses/${campus.id}/invoices/${statusInv.id}`,
        payload,
        { headers: authHeaders(), withCredentials: true },
      );
      toast.success('Invoice status updated');
      setStatusInv(null);
      fetchInvoices();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to update status'));
    } finally { setSavingStatus(false); }
  };

  const fmtN = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (s) => {
    if (!s) return '—';
    const d = new Date(String(s));
    if (isNaN(d.getTime())) return s;
    return d.toISOString().slice(0, 10);
  };
  const fmtDateTime = (s) => {
    if (!s) return '—';
    const d = new Date(String(s));
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const totals = useMemo(() => invoices.reduce(
    (a, i) => {
      a.total += Number(i.total || 0);
      const s = String(i.status || 'issued').toLowerCase();
      a.counts[s] = (a.counts[s] || 0) + 1;
      if (s !== 'paid') a.outstanding += Number(i.total || 0);
      return a;
    },
    { total: 0, outstanding: 0, counts: { issued: 0, sent: 0, paid: 0 } },
  ), [invoices]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#78716C]">Generate a consolidated monthly invoice from logged meter readings.</p>
        <Button onClick={() => setGenOpen(true)} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="generate-invoice-btn">
          <Plus size={14} className="mr-2" /> Generate Invoice
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiCard label="Invoices · Lifetime" value={invoices.length} accent="text-[#0A2540]" raw />
        <KpiCard label="Outstanding (unpaid)" value={totals.outstanding} accent="text-amber-700" />
        <KpiCard label="Total Billed (all)" value={totals.total} accent="text-[#0A2540]" />
      </div>

      <DataTable
        columns={[
          { key: 'issued_at', label: 'Issued', width: 'minmax(110px,0.9fr)', mono: true, tone: 'muted', render: (i) => fmtDate(i.issued_at) },
          { key: 'invoice_no', label: 'Invoice #', width: 'minmax(160px,1.4fr)', mono: true, tone: 'strong', render: (i) => i.invoice_no || '—' },
          { key: 'billing_month_label', label: 'Period', width: 'minmax(120px,1fr)', mono: true, render: (i) => i.billing_month_label || i.billing_month || '—' },
          { key: 'total', label: 'Total', width: 'minmax(130px,1fr)', mono: true, align: 'right',
            render: (i) => <span className="font-semibold text-[#0A2540]">{fmtN(i.total)}{i.currency ? ` ${i.currency}` : ''}</span> },
          { key: 'status', label: 'Status', width: 'minmax(110px,0.9fr)', render: (i) => {
            const s = INVOICE_STATUSES.find(x => x.value === (i.status || 'issued')) || INVOICE_STATUSES[0];
            return (
              <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full ring-1 ring-inset text-[11px] font-semibold tracking-wide ${s.bg} ${s.text} ${s.ring}`}>
                {s.label}
              </span>
            );
          } },
          { key: 'paid_on', label: 'Paid On', width: 'minmax(110px,0.8fr)', mono: true, tone: 'muted',
            render: (i) => (i.status === 'paid' && i.paid_on) ? fmtDate(i.paid_on) : '—' },
        ]}
        rows={invoices}
        loading={loading}
        rowKey={(i) => i.id}
        emptyIcon={<FileText size={20} className="text-slate-500" />}
        emptyTitle="No invoices yet"
        emptyText='Click "Generate Invoice" to consolidate a month of bills.'
        testIdPrefix="invoice"
        actionRender={(inv) => (
          <Button
            variant="ghost" size="sm"
            onClick={(e) => { e.stopPropagation(); setViewInv(inv); }}
            className="text-[#0A2540] hover:text-[#0A2540] hover:bg-[#0A2540]/10 h-8 w-8 p-0"
            data-testid={`view-invoice-${inv.id}`}
            aria-label="View details"
            title="View details"
          >
            <Eye size={14} />
          </Button>
        )}
      />

      <Dialog open={genOpen} onOpenChange={(o) => { setGenOpen(o); if (!o) setGenForm({ billing_month: defaultMonth, notes: '' }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Invoice</DialogTitle>
            <DialogDescription>
              Bundle all logged bills for the selected month into a single invoice from {campus.campus_name} to Ecowater.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGenerate} className="space-y-4 py-1">
            <div className="space-y-2">
              <Label>Billing Month</Label>
              <Select value={genForm.billing_month} onValueChange={(v) => setGenForm(p => ({ ...p, billing_month: v }))}>
                <SelectTrigger data-testid="invoice-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={genForm.notes}
                onChange={(e) => setGenForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="e.g. Q2 settlement, payable in 30 days"
                data-testid="invoice-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={generating} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="invoice-generate-confirm">
                {generating ? <Spinner size={14} className="animate-spin mr-2" /> : <DownloadSimple size={14} className="mr-2" />}
                {generating ? 'Generating...' : 'Generate'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!statusInv} onOpenChange={(o) => { if (!o) setStatusInv(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Invoice Status</DialogTitle>
            <DialogDescription>
              {statusInv?.invoice_no} · {statusInv?.billing_month_label || statusInv?.billing_month}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleStatusSave} className="space-y-4 py-1">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusForm.status} onValueChange={(v) => setStatusForm(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INVOICE_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {statusForm.status === 'paid' && (
              <div className="space-y-2">
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={statusForm.paid_on}
                  onChange={(e) => setStatusForm(p => ({ ...p, paid_on: e.target.value }))}
                  data-testid="status-paid-on"
                />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStatusInv(null)}>Cancel</Button>
              <Button type="submit" disabled={savingStatus} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="status-save">
                {savingStatus ? <Spinner size={14} className="animate-spin mr-2" /> : null}
                {savingStatus ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View invoice details */}
      <Dialog open={!!viewInv} onOpenChange={(o) => { if (!o) setViewInv(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="invoice-details-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Invoice {viewInv?.invoice_no || ''}
            </DialogTitle>
            <DialogDescription>
              {viewInv?.billing_month_label || viewInv?.billing_month || ''}
              {viewInv?.issued_at ? ` · Issued ${fmtDateTime(viewInv.issued_at)}` : ''}
            </DialogDescription>
          </DialogHeader>
          {viewInv && (
            <div className="space-y-4 py-1">
              {/* Summary tiles */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="rounded-lg border border-[#EDE6D8] bg-[#FCFAF6] p-3">
                  <p className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Water</p>
                  <p className="text-sm font-bold text-[#0A2540] mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmtN(viewInv.subtotal_water)}</p>
                </div>
                <div className="rounded-lg border border-[#EDE6D8] bg-[#FCFAF6] p-3">
                  <p className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Electricity</p>
                  <p className="text-sm font-bold text-[#0A2540] mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmtN(viewInv.subtotal_electricity)}</p>
                </div>
                <div className="rounded-lg border border-[#0A2540]/20 bg-[#0A2540]/5 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-[#0A2540]/70 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Total</p>
                  <p className="text-sm font-bold text-[#0A2540] mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {fmtN(viewInv.total)}{viewInv.currency ? ` ${viewInv.currency}` : ''}
                  </p>
                </div>
              </div>

              {/* Status / paid info */}
              <div className="flex items-center justify-between rounded-lg border border-[#EDE6D8] bg-white px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Status</span>
                  {(() => {
                    const s = INVOICE_STATUSES.find(x => x.value === (viewInv.status || 'issued')) || INVOICE_STATUSES[0];
                    return (
                      <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full ring-1 ring-inset text-[11px] font-semibold tracking-wide ${s.bg} ${s.text} ${s.ring}`}>
                        {s.label}
                      </span>
                    );
                  })()}
                </div>
                {viewInv.status === 'paid' && viewInv.paid_on && (
                  <span className="text-xs text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    Paid {viewInv.paid_on}
                  </span>
                )}
              </div>

              {/* Notes */}
              {viewInv.notes && (
                <div className="rounded-lg border border-[#EDE6D8] bg-white px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Notes</p>
                  <p className="text-sm text-[#1C1917] whitespace-pre-line">{viewInv.notes}</p>
                </div>
              )}

              {/* Line items */}
              <div className="rounded-lg border border-[#EDE6D8] overflow-hidden">
                <div className="grid grid-cols-12 gap-2 bg-[#FCFAF6] px-3 py-2 text-[10px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  <div className="col-span-4">Store</div>
                  <div className="col-span-2">Utility</div>
                  <div className="col-span-2 text-right">Usage</div>
                  <div className="col-span-2 text-right">Rate</div>
                  <div className="col-span-2 text-right">Amount</div>
                </div>
                {(viewInv.line_items || []).length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-[#78716C]">No line items.</div>
                ) : (viewInv.line_items || []).map((li, idx) => {
                  const t = (li.bill_type === 'electricity')
                    ? { label: 'Electricity', bg: 'bg-amber-100', text: 'text-amber-700', ring: 'ring-amber-200' }
                    : { label: 'Water',       bg: 'bg-blue-100',  text: 'text-blue-700',  ring: 'ring-blue-200' };
                  return (
                    <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-[#EDE6D8] text-sm items-center">
                      <div className="col-span-4 text-[#1C1917] font-medium truncate">{li.store_name || li.store_id || '—'}</div>
                      <div className="col-span-2">
                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full ring-1 ring-inset ${t.bg} ${t.text} ${t.ring}`}>
                          {t.label}
                        </span>
                      </div>
                      <div className="col-span-2 text-right text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmtN(li.usage)}</div>
                      <div className="col-span-2 text-right text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmtN(li.rate)}</div>
                      <div className="col-span-2 text-right font-semibold text-[#0A2540]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmtN(li.amount)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 w-full">
            {viewInv && (
              <Button
                variant="ghost"
                onClick={async () => {
                  const ok = window.confirm(`Delete invoice ${viewInv.invoice_no}?`);
                  if (!ok) return;
                  // Bypass the duplicate confirm in handleDelete by deleting directly.
                  try {
                    await axios.delete(
                      `${API_URL}/api/campuses/${campus.id}/invoices/${viewInv.id}`,
                      { headers: authHeaders(), withCredentials: true },
                    );
                    toast.success('Deleted');
                    setViewInv(null);
                    fetchInvoices();
                  } catch (err) {
                    toast.error(formatApiError(err, 'Failed to delete'));
                  }
                }}
                className="text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10"
                data-testid="invoice-details-delete"
              >
                <Trash size={14} className="mr-2" /> Delete
              </Button>
            )}
            <div className="flex items-center justify-end gap-2 flex-wrap">
              {viewInv && (
                <Button
                  variant="outline"
                  onClick={() => { setViewInv(null); openStatus(viewInv); }}
                  disabled={viewInv.status === 'paid'}
                  data-testid="invoice-details-edit"
                  title={viewInv.status === 'paid' ? 'Already paid' : 'Update status'}
                >
                  <PencilSimple size={14} className="mr-2" /> Edit Status
                </Button>
              )}
              {viewInv && (
                <Button
                  variant="outline"
                  onClick={() => handleDownload(viewInv)}
                  disabled={downloadingId === viewInv.id}
                  data-testid="invoice-details-download"
                >
                  {downloadingId === viewInv.id ? <Spinner size={14} className="animate-spin mr-2" /> : <FilePdf size={14} className="mr-2" />}
                  Download PDF
                </Button>
              )}
              <Button onClick={() => setViewInv(null)}>Close</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ====== ROYALTIES (scoped, persistent) ====== */
function RoyaltiesScoped({ campus }) {
  const [royalties, setRoyalties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    beneficiary: '', period_start: '', period_end: '',
    base_revenue: '', rate_pct: '5', amount: '',
    status: 'owed', paid_on: '', reference: '',
  });

  const fetchRoyalties = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/campuses/${campus.id}/royalties`,
        { headers: authHeaders(), withCredentials: true });
      setRoyalties(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load royalties'));
    } finally { setLoading(false); }
  }, [campus.id]);

  useEffect(() => { fetchRoyalties(); }, [fetchRoyalties]);

  const totals = useMemo(() => royalties.reduce((a, r) => {
    a[r.status] = (a[r.status] || 0) + Number(r.amount || 0);
    return a;
  }, { paid: 0, owed: 0, overdue: 0 }), [royalties]);

  const reset = () => setForm({
    beneficiary: '', period_start: '', period_end: '',
    base_revenue: '', rate_pct: '5', amount: '',
    status: 'owed', paid_on: '', reference: '',
  });

  const computeAmount = (base, pct) => Math.round(Number(base || 0) * Number(pct || 0)) / 100;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.beneficiary) { toast.error('Beneficiary required'); return; }
    const amount = form.amount ? Number(form.amount) : computeAmount(form.base_revenue, form.rate_pct);
    try {
      await axios.post(`${API_URL}/api/campuses/${campus.id}/royalties`,
        {
          ...form, amount,
          base_revenue: Number(form.base_revenue || 0),
          rate_pct: Number(form.rate_pct || 0),
        },
        { headers: authHeaders(), withCredentials: true });
      toast.success('Royalty recorded');
      setOpen(false); reset();
      fetchRoyalties();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save'));
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm('Delete this royalty entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/campuses/${campus.id}/royalties/${entry.id}`,
        { headers: authHeaders(), withCredentials: true });
      toast.success('Deleted');
      fetchRoyalties();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#78716C]">Track royalty obligations and settlements for this campus.</p>
        <Button onClick={() => setOpen(true)} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="add-royalty-btn">
          <Plus size={14} className="mr-2" /> Add Royalty
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <KpiCard label="Paid (lifetime)" value={totals.paid}    accent="text-emerald-700" />
        <KpiCard label="Owed"            value={totals.owed}    accent="text-amber-700" />
        <KpiCard label="Overdue"         value={totals.overdue} accent="text-rose-700" />
      </div>

      <DataTable
        columns={[
          { key: 'beneficiary', label: 'Beneficiary', width: 'minmax(160px,1.3fr)', tone: 'strong' },
          { key: 'period', label: 'Period', width: 'minmax(170px,1.2fr)', mono: true, tone: 'muted',
            render: (r) => `${r.period_start || '—'} → ${r.period_end || '—'}` },
          { key: 'rate_pct', label: 'Rate', width: 'minmax(80px,0.6fr)', mono: true, tone: 'muted', render: (r) => `${r.rate_pct ?? 0}%` },
          { key: 'amount', label: 'Amount', width: 'minmax(120px,1fr)', mono: true,
            render: (r) => Number(r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
          { key: 'paid_on', label: 'Paid On', width: 'minmax(110px,0.9fr)', mono: true, tone: 'muted', render: (r) => r.paid_on || '—' },
          { key: 'status', label: 'Status', width: 'minmax(110px,0.9fr)', render: (r) => {
            const s = ROYALTY_STATUSES.find(x => x.value === r.status) || ROYALTY_STATUSES[0];
            return (
              <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full ring-1 ring-inset text-[11px] font-semibold tracking-wide ${s.bg} ${s.text} ${s.ring}`}>
                {s.label}
              </span>
            );
          } },
        ]}
        rows={royalties}
        loading={loading}
        rowKey={(r) => r.id}
        emptyIcon={<Money size={20} className="text-slate-500" />}
        emptyTitle="No royalties yet"
        emptyText='Click "Add Royalty" to log a payment for this campus.'
        testIdPrefix="royalty"
        actionRender={(r) => (
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(r); }}
            className="text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10 h-8 w-8 p-0" aria-label="Delete">
            <Trash size={14} />
          </Button>
        )}
      />

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Royalty</DialogTitle>
            <DialogDescription>Record a royalty payment or obligation for {campus.campus_name}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-1">
            <FField label="Beneficiary" value={form.beneficiary} onChange={v => setForm(p => ({ ...p, beneficiary: v }))} testid="royalty-beneficiary" />
            <div className="grid grid-cols-2 gap-3">
              <FField label="Period Start" type="date" value={form.period_start} onChange={v => setForm(p => ({ ...p, period_start: v }))} testid="royalty-start" />
              <FField label="Period End"   type="date" value={form.period_end}   onChange={v => setForm(p => ({ ...p, period_end: v }))}   testid="royalty-end" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <FField label="Base Revenue"     type="number" value={form.base_revenue} onChange={v => setForm(p => ({ ...p, base_revenue: v }))} testid="royalty-base" />
              <FField label="Rate (%)"          type="number" value={form.rate_pct}     onChange={v => setForm(p => ({ ...p, rate_pct: v }))}    testid="royalty-rate" />
              <FField label="Amount (override)" type="number" value={form.amount}       onChange={v => setForm(p => ({ ...p, amount: v }))}      testid="royalty-amount" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="royalty-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROYALTY_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <FField label="Paid On" type="date" value={form.paid_on} onChange={v => setForm(p => ({ ...p, paid_on: v }))} testid="royalty-paidon" />
            </div>
            <FField label="Reference" value={form.reference} onChange={v => setForm(p => ({ ...p, reference: v }))} testid="royalty-ref" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
              <Button type="submit" className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="royalty-save">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, accent, raw }) {
  const display = raw
    ? (value ?? '—')
    : Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
      <p className={`mt-2 text-2xl font-bold font-mono truncate ${accent}`}>{display}</p>
    </div>
  );
}
